/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateServiceOrderItemRequest } from '../models/create-service-order-item-request';
import { CreateServiceOrderRequest } from '../models/create-service-order-request';
import { GetServiceOrderResponse } from '../models/get-service-order-response';
import { ServiceOrderResponse } from '../models/service-order-response';
import { ServiceOrderStatus } from '../models/service-order-status';
import { ServiceOrderType } from '../models/service-order-type';
import { UpdateServiceOrderRequest } from '../models/update-service-order-request';

/**
 * Service order APIs
 */
@Injectable({ providedIn: 'root' })
export class ServiceOrderService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllServiceOrders()` */
  static readonly GetAllServiceOrdersPath = '/api/v1/service-orders';

  /**
   * Get list of service order.
   *
   * Gets the list of service orders.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllServiceOrders()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllServiceOrders$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - invoice
       *     - invoiceNumber
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from?: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to?: string;

      /**
       * This adds a filter to the service order list
       *  - service order type name
       */
      serviceOrderType?: ServiceOrderType;

      /**
       * This adds a filter to the service order list
       *  - service order status name
       */
      serviceOrderStatus?: ServiceOrderStatus;

      /**
       * This enables filtering of aircraft service items based on the aircraft id
       */
      aircraftId?: number;

      /**
       * This enables filtering of service order items based on the client id
       */
      clientId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetServiceOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceOrderService.GetAllServiceOrdersPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
      rb.query('serviceOrderType', params.serviceOrderType, {});
      rb.query('serviceOrderStatus', params.serviceOrderStatus, {});
      rb.query('aircraftId', params.aircraftId, {});
      rb.query('clientId', params.clientId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetServiceOrderResponse>;
        })
      );
  }

  /**
   * Get list of service order.
   *
   * Gets the list of service orders.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllServiceOrders$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllServiceOrders(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - invoice
       *     - invoiceNumber
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from?: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to?: string;

      /**
       * This adds a filter to the service order list
       *  - service order type name
       */
      serviceOrderType?: ServiceOrderType;

      /**
       * This adds a filter to the service order list
       *  - service order status name
       */
      serviceOrderStatus?: ServiceOrderStatus;

      /**
       * This enables filtering of aircraft service items based on the aircraft id
       */
      aircraftId?: number;

      /**
       * This enables filtering of service order items based on the client id
       */
      clientId?: number;
    },
    context?: HttpContext
  ): Observable<GetServiceOrderResponse> {
    return this.getAllServiceOrders$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetServiceOrderResponse>
        ): GetServiceOrderResponse => r.body
      )
    );
  }

  /** Path part for operation `createServiceOrder()` */
  static readonly CreateServiceOrderPath = '/api/v1/service-orders';

  /**
   * Create new service order.
   *
   * Create a new service item to the backend.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createServiceOrder()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createServiceOrder$Response(
    params: {
      body: CreateServiceOrderRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ServiceOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceOrderService.CreateServiceOrderPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ServiceOrderResponse>;
        })
      );
  }

  /**
   * Create new service order.
   *
   * Create a new service item to the backend.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createServiceOrder$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createServiceOrder(
    params: {
      body: CreateServiceOrderRequest;
    },
    context?: HttpContext
  ): Observable<ServiceOrderResponse> {
    return this.createServiceOrder$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ServiceOrderResponse>): ServiceOrderResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getServiceOrderById()` */
  static readonly GetServiceOrderByIdPath = '/api/v1/service-orders/{id}';

  /**
   * fetch service order details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getServiceOrderById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceOrderById$Response(
    params: {
      /**
       * The service order id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ServiceOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceOrderService.GetServiceOrderByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ServiceOrderResponse>;
        })
      );
  }

  /**
   * fetch service order details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getServiceOrderById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceOrderById(
    params: {
      /**
       * The service order id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<ServiceOrderResponse> {
    return this.getServiceOrderById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ServiceOrderResponse>): ServiceOrderResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `updateServiceOrderById()` */
  static readonly UpdateServiceOrderByIdPath = '/api/v1/service-orders/{id}';

  /**
   * update service order item.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateServiceOrderById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateServiceOrderById$Response(
    params: {
      /**
       * The service order id that needs to be update
       */
      id: number;
      body: UpdateServiceOrderRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ServiceOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceOrderService.UpdateServiceOrderByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ServiceOrderResponse>;
        })
      );
  }

  /**
   * update service order item.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateServiceOrderById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateServiceOrderById(
    params: {
      /**
       * The service order id that needs to be update
       */
      id: number;
      body: UpdateServiceOrderRequest;
    },
    context?: HttpContext
  ): Observable<ServiceOrderResponse> {
    return this.updateServiceOrderById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ServiceOrderResponse>): ServiceOrderResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `addServiceItem()` */
  static readonly AddServiceItemPath =
    '/api/v1/service-orders/{id}/service-item';

  /**
   * Add service item to service order.
   *
   * Add service item to service order
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addServiceItem()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addServiceItem$Response(
    params: {
      /**
       * The service order id that needs to be add service item
       */
      id: number;
      body: CreateServiceOrderItemRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ServiceOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ServiceOrderService.AddServiceItemPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ServiceOrderResponse>;
        })
      );
  }

  /**
   * Add service item to service order.
   *
   * Add service item to service order
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addServiceItem$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addServiceItem(
    params: {
      /**
       * The service order id that needs to be add service item
       */
      id: number;
      body: CreateServiceOrderItemRequest;
    },
    context?: HttpContext
  ): Observable<ServiceOrderResponse> {
    return this.addServiceItem$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<ServiceOrderResponse>): ServiceOrderResponse =>
          r.body
      )
    );
  }
}
